import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import * as dashboardService from "../services/dashboard.services";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { DateRangePicker, Placeholder, SelectPicker } from "rsuite";
import { getBarChartDefaultOptions, usePermission } from "../../../utils/commonfunction";

const Dashboard = () => {
  const { t } = useTranslation();
  const { can } = usePermission();
  const [showDashboardCount, setShowDashboardCount] = useState({});

  /** Bookings Overview */
  const [bookingsOverviewTimeLimit, setBookingsOverviewTimeLimit] = useState("this_week");
  const [bookingOverviewCustomRange, setBookingOverviewCustomRange] = useState(null);
  const [bookingOverviewLoader, setBookingOverviewLoader] = useState(false);
  const [bookingOverviewData, setBookingOverviewData] = useState({
    series: [{ name: "Bookings", data: [] }],
    options: {
      chart: { type: "bar", toolbar: { show: false } },
      yaxis: { labels: { formatter: (val) => val.toFixed(0) } },
      xaxis: { categories: [] },
      ...getBarChartDefaultOptions(),
    }
  });
  const [showBookingOverviewDatePicker, setShowBookingOverviewDatePicker] = useState(false);

  useEffect(() => {
    if (bookingsOverviewTimeLimit !== "custom_range" || bookingOverviewCustomRange) {
      setBookingOverviewLoader(true);
      dashboardService.BookingsOverview(bookingsOverviewTimeLimit, JSON.stringify(bookingOverviewCustomRange)).then((response) => {
        let temp = { ...bookingOverviewData };
        temp.series[0].data = response?.data?.counts || [];
        temp.options.xaxis.categories = response?.data?.labels || [];
        setBookingOverviewData(temp);
        setBookingOverviewLoader(false);
      }).catch((error) => console.log("error ====> ", error));
    }
  }, [bookingsOverviewTimeLimit, bookingOverviewCustomRange]);

  const handleBookingOverviewSelect = (value) => {
    setBookingsOverviewTimeLimit(value);
    if (value === "custom_range") {
      setShowBookingOverviewDatePicker(true);
    } else {
      setShowBookingOverviewDatePicker(false);
      setBookingOverviewCustomRange("");
    }
  };

  /** Revenue Overview */
  const [revenueOverviewTimeLimit, setRevenueOverviewTimeLimit] = useState("this_week");
  const [revenueOverviewCustomRange, setRevenueOverviewCustomRange] = useState(null);
  const [revenueOverviewLoader, setRevenueOverviewLoader] = useState(false);
  const [revenueOverviewData, setRevenueOverviewData] = useState({
    series: [{ name: "Revenue", data: [] }],
    options: {
      chart: { type: "bar", toolbar: { show: false } },
      xaxis: { categories: [] },
      tooltip: { y: { formatter: (val) => `$${val}` } },
      yaxis: { labels: { formatter: (val) => `$${val}` } },
      ...getBarChartDefaultOptions(),
    }
  });
  const [showRevenueOverviewDatePicker, setShowRevenueOverviewDatePicker] = useState(false);

  useEffect(() => {
    if (revenueOverviewTimeLimit !== "custom_range" || revenueOverviewCustomRange) {
      setRevenueOverviewLoader(true);
      dashboardService.RevenueOverview(revenueOverviewTimeLimit, JSON.stringify(revenueOverviewCustomRange)).then((response) => {
        let temp = { ...revenueOverviewData };
        temp.series[0].data = response?.data?.counts || [];
        temp.options.xaxis.categories = response?.data?.labels || [];
        setRevenueOverviewData(temp);
        setRevenueOverviewLoader(false);
      }).catch((error) => console.log("error ====> ", error));
    }
  }, [revenueOverviewTimeLimit, revenueOverviewCustomRange]);

  const handleRevenueOverviewSelect = (value) => {
    setRevenueOverviewTimeLimit(value);
    if (value === "custom_range") {
      setShowRevenueOverviewDatePicker(true);
    } else {
      setShowRevenueOverviewDatePicker(false);
      setRevenueOverviewCustomRange("");
    }
  };

  useEffect(() => {
    dashboardService.Counts().then((response) => {
      setShowDashboardCount(response && response.data ? response.data : {});
    }).catch((error) => console.log("error ====> ", error));
  }, []);

  const chartFilterOptions = [
    { label: "Last 7 days", value: "this_week" },
    { label: "Last 30 days", value: "last_month" },
    { label: "Last year", value: "last_year" },
    { label: "Custom range", value: "custom_range" },
  ];

  return (
    <>
      <div className="row row-sm animation_fade">
        <div className="col-sm-12 col-lg-12 col-xl-12">
          <div className="row row-sm">

            {/* Babe Users */}
            {can("/admin/babe-user/list") && (
              <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
                <Link to={`/admin/babe-user/list/1`}>
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <div className="card-item">
                        <div className="card-item-icon card-icon cp">
                          <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-people-fill" viewBox="0 0 16 16">
                            <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6" />
                          </svg>
                        </div>
                        <div className="card-item-title mb-2">
                          <label className="main-content-label tx-13 font-weight-bold mb-1">{t("sidebar_link_users")}</label>
                        </div>
                        <div className="card-item-body">
                          <div className="card-item-stat">
                            <h4 className="font-weight-bold">{showDashboardCount?.users || "0"}</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Babe Associates */}
            {can("/admin/babe-associate/list") && (
              <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
                <Link to={`/admin/babe-associate/list/1`}>
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <div className="card-item">
                        <div className="card-item-icon card-icon cp">
                          <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-people-fill" viewBox="0 0 16 16">
                            <path d="M1 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6" />
                            <path fillRule="evenodd" d="M13.5 5a.5.5 0 0 1 .5.5V7h1.5a.5.5 0 0 1 0 1H14v1.5a.5.5 0 0 1-1 0V8h-1.5a.5.5 0 0 1 0-1H13V5.5a.5.5 0 0 1 .5-.5" />
                          </svg>
                        </div>
                        <div className="card-item-title mb-2">
                          <label className="main-content-label tx-13 font-weight-bold mb-1">{t("sidebar_link_associate")}</label>
                        </div>
                        <div className="card-item-body">
                          <div className="card-item-stat">
                            <h4 className="font-weight-bold">{showDashboardCount?.associate || "0"}</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* New Inquiries */}
            {can("/admin/inquiries/list") && (
              <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
                <Link to={`/admin/inquiries/list/1?status=new`}>
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <div className="card-item">
                        <div className="card-item-icon card-icon cp">
                          <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-person-raised-hand" viewBox="0 0 16 16">
                            <path d="M6 6.207v9.043a.75.75 0 0 0 1.5 0V10.5a.5.5 0 0 1 1 0v4.75a.75.75 0 0 0 1.5 0v-8.5a.25.25 0 1 1 .5 0v2.5a.75.75 0 0 0 1.5 0V6.5a3 3 0 0 0-3-3H6.236a1 1 0 0 1-.447-.106l-.33-.165A.83.83 0 0 1 5 2.488V.75a.75.75 0 0 0-1.5 0v2.083c0 .715.404 1.37 1.044 1.689L5.5 5c.32.32.5.754.5 1.207" />
                            <path d="M8 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3" />
                          </svg>
                        </div>
                        <div className="card-item-title mb-2">
                          <label className="main-content-label tx-13 font-weight-bold mb-1">New Inquiries</label>
                        </div>
                        <div className="card-item-body">
                          <div className="card-item-stat">
                            <h4 className="font-weight-bold">{showDashboardCount?.newInquiries || "0"}</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Total Job Searches */}
            {can("/admin/jobs-management/list") && (
              <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
                <Link to={`/admin/jobs-management/list/1`}>
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <div className="card-item">
                        <div className="card-item-icon card-icon cp">
                          <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-search-heart-fill" viewBox="0 0 16 16">
                            <path d="M6.5 13a6.47 6.47 0 0 0 3.845-1.258h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1A6.47 6.47 0 0 0 13 6.5 6.5 6.5 0 0 0 6.5 0a6.5 6.5 0 1 0 0 13m0-8.518c1.664-1.673 5.825 1.254 0 5.018-5.825-3.764-1.664-6.69 0-5.018" />
                          </svg>
                        </div>
                        <div className="card-item-title mb-2">
                          <label className="main-content-label tx-13 font-weight-bold mb-1">Total Job Searches</label>
                        </div>
                        <div className="card-item-body">
                          <div className="card-item-stat">
                            <h4 className="font-weight-bold">{showDashboardCount?.totalJobSearches || "0"}</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Pending Bookings */}
            {can("/admin/pending-appointments/list") && (
              <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
                <Link to={`/admin/pending-appointments/list/1?status=confirmed`}>
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <div className="card-item">
                        <div className="card-item-icon card-icon cp">
                          <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-journal-bookmark-fill" viewBox="0 0 16 16">
                            <path fillRule="evenodd" d="M8.646 5.646a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1 0 .708l-2 2a.5.5 0 0 1-.708-.708L10.293 8 8.646 6.354a.5.5 0 0 1 0-.708m-1.292 0a.5.5 0 0 0-.708 0l-2 2a.5.5 0 0 0 0 .708l2 2a.5.5 0 0 0 .708-.708L5.707 8l1.647-1.646a.5.5 0 0 0 0-.708" />
                            <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-1h1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v1H1V2a2 2 0 0 1 2-2" />
                            <path d="M1 5v-.5a.5.5 0 0 1 1 0V5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0V8h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1z" />
                          </svg>
                        </div>
                        <div className="card-item-title mb-2">
                          <label className="main-content-label tx-13 font-weight-bold mb-1">Pending bookings</label>
                        </div>
                        <div className="card-item-body">
                          <div className="card-item-stat">
                            <h4 className="font-weight-bold">{showDashboardCount?.pendingBookings || "0"}</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Cancelled Bookings */}
            {can("/admin/appointments/list") && (
              <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
                <Link to={`/admin/appointments/list/1?status=cancelled`}>
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <div className="card-item">
                        <div className="card-item-icon card-icon cp">
                          <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-journal-x" viewBox="0 0 16 16">
                            <path fillRule="evenodd" d="M6.146 6.146a.5.5 0 0 1 .708 0L8 7.293l1.146-1.147a.5.5 0 1 1 .708.708L8.707 8l1.147 1.146a.5.5 0 0 1-.708.708L8 8.707 6.854 9.854a.5.5 0 0 1-.708-.708L7.293 8 6.146 6.854a.5.5 0 0 1 0-.708" />
                            <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-1h1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v1H1V2a2 2 0 0 1 2-2" />
                            <path d="M1 5v-.5a.5.5 0 0 1 1 0V5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0V8h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1z" />
                          </svg>
                        </div>
                        <div className="card-item-title mb-2">
                          <label className="main-content-label tx-13 font-weight-bold mb-1">Cancelled bookings</label>
                        </div>
                        <div className="card-item-body">
                          <div className="card-item-stat">
                            <h4 className="font-weight-bold">{showDashboardCount?.cancelledBookings || "0"}</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Completed Bookings */}
            {can("/admin/appointments/list") && (
              <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
                <Link to={`/admin/appointments/list/1?status=completed`}>
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <div className="card-item">
                        <div className="card-item-icon card-icon cp">
                          <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-journal-check" viewBox="0 0 16 16">
                            <path fillRule="evenodd" d="M10.854 6.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 8.793l2.646-2.647a.5.5 0 0 1 .708 0" />
                            <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-1h1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v1H1V2a2 2 0 0 1 2-2" />
                            <path d="M1 5v-.5a.5.5 0 0 1 1 0V5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0V8h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1z" />
                          </svg>
                        </div>
                        <div className="card-item-title mb-2">
                          <label className="main-content-label tx-13 font-weight-bold mb-1">Completed bookings</label>
                        </div>
                        <div className="card-item-body">
                          <div className="card-item-stat">
                            <h4 className="font-weight-bold">{showDashboardCount?.completedBookings || "0"}</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Pending Provider Verifications */}
            {can("/admin/babe-associate/list") && (
              <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
                <Link to={`/admin/babe-associate/list/1`}>
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <div className="card-item">
                        <div className="card-item-icon card-icon cp">
                          <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-person-fill-exclamation" viewBox="0 0 16 16">
                            <path d="M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0m-9 8c0 1 1 1 1 1h5.256A4.5 4.5 0 0 1 8 12.5a4.5 4.5 0 0 1 1.544-3.393Q8.844 9.002 8 9c-5 0-6 3-6 4" />
                            <path d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0m-3.5-2a.5.5 0 0 0-.5.5v1.5a.5.5 0 0 0 1 0V11a.5.5 0 0 0-.5-.5m0 4a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1" />
                          </svg>
                        </div>
                        <div className="card-item-title mb-2">
                          <label className="main-content-label tx-13 font-weight-bold mb-1">Pending Provider Verifications</label>
                        </div>
                        <div className="card-item-body">
                          <div className="card-item-stat">
                            <h4 className="font-weight-bold">{showDashboardCount?.pendingProviderVerifications || "0"}</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

          </div>
        </div>

        {/* Charts */}
        <div className="col-sm-12 col-lg-12 col-xl-12 admin-placeholder">
          <div className="row row-sm">

            {/* Bookings Chart */}
            {can("/admin/appointments/list") && (
              <div className="col-lg-6">
                <div className="card custom-card mg-b-20">
                  <div className="card-body">
                    <div className="card-header border-bottom-0 pt-0 pl-0 pr-0 d-flex">
                      <div>
                        <label className="main-content-label mb-4">Bookings Overview</label>
                      </div>
                      <div className="ml-auto d-flex align-items-center">
                        <SelectPicker
                          defaultValue={"last_month"}
                          cleanable={false}
                          onSelect={handleBookingOverviewSelect}
                          size="sm"
                          data={chartFilterOptions}
                          className="dashboard-filter-select"
                          searchable={false}
                        />
                        {showBookingOverviewDatePicker && (
                          <div className="form-group mb-0 ms-2 rangepicker_container filter_design">
                            <DateRangePicker
                              editable={false}
                              size="sm"
                              placeholder={"Select Date"}
                              placement="bottomEnd"
                              disabledDate={(date) => {
                                const year = date.getFullYear();
                                return year < 2000 || year > 2099;
                              }}
                              onChange={(e) => {
                                setBookingOverviewCustomRange(
                                  e && e.length === 2 ? { start: e[0], end: e[1] } : null
                                );
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {bookingOverviewLoader ? (
                      <Placeholder.Graph active />
                    ) : (
                      <Chart options={bookingOverviewData.options} series={bookingOverviewData.series} type="bar" height={350} />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Revenue Chart */}
            {can("/admin/subscription/transaction/list") && (
              <div className="col-lg-6">
                <div className="card custom-card mg-b-20">
                  <div className="card-body">
                    <div className="card-header border-bottom-0 pt-0 pl-0 pr-0 d-flex">
                      <div>
                        <label className="main-content-label mb-4">Revenue Overview</label>
                      </div>
                      <div className="ml-auto d-flex align-items-center">
                        <SelectPicker
                          defaultValue={"last_month"}
                          cleanable={false}
                          onSelect={handleRevenueOverviewSelect}
                          size="sm"
                          data={chartFilterOptions}
                          className="dashboard-filter-select"
                          searchable={false}
                        />
                        {showRevenueOverviewDatePicker && (
                          <div className="form-group mb-0 ms-2 rangepicker_container filter_design">
                            <DateRangePicker
                              size="sm"
                              placeholder={"Select Date"}
                              editable={false}
                              placement="bottomEnd"
                              disabledDate={(date) => {
                                const year = date.getFullYear();
                                return year < 2000 || year > 2099;
                              }}
                              onChange={(e) => {
                                setRevenueOverviewCustomRange(
                                  e && e.length === 2 ? { start: e[0], end: e[1] } : null
                                );
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {revenueOverviewLoader ? (
                      <Placeholder.Graph active />
                    ) : (
                      <Chart options={revenueOverviewData.options} series={revenueOverviewData.series} type="bar" height={350} />
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

      </div>
    </>
  );
};

export default Dashboard;